import { useSnackbar } from 'notistack';
import * as React from 'react';
import { FieldErrors, UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { WithFormErrors } from '~/components/GenericForm/FormError';
import { sendMessageToHR } from '~/data/company';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';

export interface SendMessageToHRFormValues extends WithFormErrors {
  contacts?: string[];
  message: string;
}

export interface UseSendMessageToHRDialogContentOutput {
  isSubmitting: boolean;
  register: UseFormRegister<SendMessageToHRFormValues>;
  contacts?: string[];
  handleContactsChange: (event: React.ChangeEvent<{}>, value: string[]) => void;
  handleFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  errors: FieldErrors<SendMessageToHRFormValues>;
}

export const useSendMessageToHRDialogContent = (
  onClose: () => void,
  companyHrContacts?: string[],
): UseSendMessageToHRDialogContentOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const validationSchema = React.useMemo(() => {
    return yup.object().shape<SendMessageToHRFormValues>({
      contacts:
        companyHrContacts && companyHrContacts.length > 1
          ? yup.array<string>().required(t('fieldIsRequired'))
          : yup.array<string>(),
      message: yup.string().required(t('fieldIsRequired')),
    });
  }, [t, companyHrContacts]);

  const resolver = useYupValidationResolver(validationSchema);

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<SendMessageToHRFormValues>({
    defaultValues: {
      contacts: (companyHrContacts || []).slice(0, 1),
    },
    resolver,
  });

  React.useEffect(() => {
    register('contacts');
  }, [register]);

  const handleContactsChange = React.useCallback(
    (_: React.ChangeEvent<{}>, value: string[]) => {
      setValue('contacts', value);
    },
    [setValue],
  );

  const handleFormSubmit = React.useMemo(() => {
    return handleSubmit((data) => {
      setIsSubmitting(true);

      (async () => {
        try {
          await sendMessageToHR(
            data.message,
            data.contacts?.length ? data.contacts : undefined,
          );

          enqueueSnackbar(t('successfullySentMessageToHR'), {
            variant: 'success',
          });

          onClose();
        } catch {
          setError('form', {
            type: 'validation',
            message: t('failedToSendMessageToHR'),
          });
        } finally {
          setIsSubmitting(false);
        }
      })();
    });
  }, [handleSubmit, setIsSubmitting, enqueueSnackbar, t, onClose, setError]);

  return {
    isSubmitting,
    register,
    contacts: watch('contacts'),
    handleContactsChange,
    handleFormSubmit,
    errors,
  };
};
